import React, { FunctionComponent, useEffect, useState } from 'react';
import { Formik } from 'formik';

import { ISignUpForm, SignUpFormDefinition } from '@jetslash/market-frontend-shared-core/src/forms/signUp';
import { postSignUp } from '@jetslash/market-frontend-shared-core/src/api/userApi';
import { IFormDefinition } from '@jetslash/market-frontend-shared-core/src/forms/formDefinitionInterface';
import { ValidationErrorMessages } from '@jetslash/market-frontend-shared-core/src/utilities/form/types';
import {
  EVENT_SIGN_UP_STARTED,
  EVENT_SIGN_UP_STEP_VIEWED,
} from '@jetslash/market-frontend-shared-core/src/utilities/eventTracking/events';
import SignUpStepPropGroup from '@jetslash/market-frontend-shared-core/src/utilities/eventTracking/propertyGroupSerializers/SignUpStepPropGroup';

import { setInMemoryToken } from '@/utility/auth/authWrapper';
import { parseErrorMessagesFromResponse } from '@jetslash/market-frontend-shared-core/src/utilities/form/formErrorsFromErrorResponse';
import useSignUpWizard from '@/hooks/useSignUpWizard';
import InputField from '../fields/InputField';
import Form from '../fields/Form';
import Col from '../../layout/Col';
import Row from '../../layout/Row';
import CTAButton from '../../design/CTAButton';
import FormContainer from '../FormContainer';
import FormErrorDisplay from '../FormErrorDisplay';

import { track } from '../../../utility/eventTracking/eventTracker';

interface ISignUpFormContainerProps {
  loading: boolean;
  errors: ValidationErrorMessages;
}

const SignUpFormContainer: FunctionComponent<ISignUpFormContainerProps> = ({ loading, errors }) => {
  const { signUpStepStartRequest, signUpStepSuccess, signUpStepFailure } = useSignUpWizard();
  const formDefinition = new SignUpFormDefinition();
  const [initialFormValues] = useState<ISignUpForm>(formDefinition.initialValues());

  useEffect(() => {
    track(EVENT_SIGN_UP_STARTED, {});
    track(
      EVENT_SIGN_UP_STEP_VIEWED,
      SignUpStepPropGroup.create({} as ISignUpForm, SignUpStepPropGroup.signUpSteps.INITIAL_FORM),
    );
  }, []);

  const onSignUpSubmit = async (
    { firstName, lastName, email, password, passwordConfirmation }: ISignUpForm,
    { resetForm },
  ) => {
    const userSignUpData = {
      firstName,
      lastName,
      email,
      password,
      passwordConfirmation,
    };

    signUpStepStartRequest();
    try {
      const response = await postSignUp(userSignUpData);
      setInMemoryToken(response.data.jwtToken);
      signUpStepSuccess();
    } catch (err) {
      const formErrors = parseErrorMessagesFromResponse(err, formDefinition.HUMANIZATION_MAP);
      signUpStepFailure(formErrors);
      resetForm({
        firstName,
        lastName,
        email,
        password: '',
        passwordConfirmation: '',
      });
    }
  };

  return (
    <SignUpForm
      onSubmit={onSignUpSubmit}
      loading={loading}
      validationErrors={errors}
      initialFormValues={initialFormValues}
    />
  );
};

interface IProps {
  onSubmit: any;
  loading: boolean;
  validationErrors?: ValidationErrorMessages;
  initialFormValues: ISignUpForm;
}

class SignUpForm extends React.Component<IProps> {
  // eslint-disable-next-line react/no-unused-class-component-methods
  textInput: { [key: string]: any };

  formDefinition: IFormDefinition;

  constructor(props: IProps) {
    super(props);
    // eslint-disable-next-line react/no-unused-class-component-methods
    this.textInput = {};
    this.formDefinition = new SignUpFormDefinition();
  }

  render() {
    const { onSubmit, loading, validationErrors, initialFormValues } = this.props;

    return (
      <Formik
        validationSchema={this.formDefinition.validationSchema}
        initialValues={initialFormValues}
        onSubmit={onSubmit}
        validateOnMount
      >
        {({ handleSubmit, isValid }) => (
          <FormContainer style={{ marginLeft: 5, marginRight: 5 }}>
            <FormErrorDisplay errors={validationErrors} />
            <Form>
              <Row>
                <Col span={12}>
                  <InputField
                    name="firstName"
                    loading={loading}
                    placeholder="First name"
                    type="text"
                    autoComplete="given-name"
                  />
                </Col>
                <Col span={12}>
                  <InputField
                    name="lastName"
                    loading={loading}
                    placeholder="Last name"
                    type="text"
                    autoComplete="family-name"
                  />
                </Col>
              </Row>
              <InputField
                name="email"
                loading={loading}
                placeholder="Email address"
                type="email"
                autoComplete="email"
              />
              <InputField name="password" loading={loading} placeholder="Password" type="password" />
              <InputField
                name="passwordConfirmation"
                loading={loading}
                placeholder="Confirm password"
                type="password"
              />
              <Row style={{ marginLeft: 5, marginRight: 5, marginTop: 10 }}>
                <Col span={24}>
                  <CTAButton onClick={handleSubmit} disabled={!isValid || loading} text="Sign up" />
                </Col>
              </Row>
            </Form>
          </FormContainer>
        )}
      </Formik>
    );
  }
}

export default SignUpFormContainer;
